import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit2, X, Check, Trash2, Save, Search } from 'lucide-react';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUserEid, setEditingUserEid] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    eid: '',
    username: '',
    email: '',
    password: '',
    right: 'ADMIN'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const filtered = users.filter(user => 
      user.eid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.right?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/users/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        }
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      let users = data.map(e=>!e.is_superuser&&e.right==='ADMIN'?{...e, right:'ONBOARDING'}:e)
      setUsers(users);
      setFilteredUsers(users);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!formData.eid || !formData.username || !formData.email || !formData.password) {
      setError('All fields are required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/users/create-admin`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        body: JSON.stringify({
          ...formData,
          right: formData.right==="ONBOARDING"?"ADMIN":formData.right,
          is_active: true,
          is_superuser: formData.right==="ADMIN"?true:false
        })
      });

      if (!response.ok) throw new Error('Failed to create user');
      
      setSuccess('User created successfully!');
      setFormData({ eid: '', username: '', email: '', password: '', right: 'ADMIN' });
      setShowCreateForm(false);
      fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (user) => {
    setEditingUserEid(user.eid);
    setEditFormData({
      id: user.id,
      eid: user.eid,
      right: user.right,
      is_active: user.is_active
    });
  };

  const handleCancelEdit = () => {
    setEditingUserEid(null);
    setEditFormData({});
  };

  const handleSaveEdit = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/users/${editFormData.eid}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        body: JSON.stringify({
          eid: editFormData.eid,
          right: editFormData.right==="ONBOARDING"?"ADMIN":editFormData.right,
          is_active: editFormData.is_active,
          is_superuser: editFormData.right==="ADMIN"?true:false
        })
      });

      if (!response.ok) throw new Error('Failed to update user');
      
      setSuccess('User updated successfully!');
      setEditingUserEid(null);
      setEditFormData({});
      fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (eid) => {
    if (!window.confirm(`Are you sure you want to delete user with EID: ${eid}?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/users/${eid}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        }
      });

      if (!response.ok) throw new Error('Failed to delete user');
      
      setSuccess('User deleted successfully!');
      fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-indigo-600" />
              <h1 className="text-3xl font-bold text-gray-800">User Management</h1>
            </div>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition"
            >
              {showCreateForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              {showCreateForm ? 'Cancel' : 'Create User'}
            </button>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              {success}
            </div>
          )}

          {showCreateForm && (
            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-800">Create New User</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">EID</label>
                  <input
                    type="text"
                    value={formData.eid}
                    onChange={(e) => setFormData({ ...formData, eid: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Right</label>
                  <select
                    value={formData.right}
                    onChange={(e) => setFormData({ ...formData, right: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="ADMIN">ADMIN</option>
                    <option value="EMPLOYEE">EMPLOYEE</option>
                    <option value="ONBOARDING">ONBOARDING</option>
                  </select>
                </div>
              </div>
              <button
                onClick={handleCreateUser}
                disabled={loading}
                className="mt-4 bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition disabled:bg-gray-400"
              >
                {loading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by EID, username, email, or right..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="overflow-auto max-h-[32rem]">
            <table className="w-full overflow-auto">
              <thead className="bg-indigo-600 text-white sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold">EID</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Username</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Email</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Right</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Active</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading && users.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      Loading users...
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.eid} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{user.eid}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{user.username}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{user.email}</td>
                      <td className="px-6 py-4 text-sm">
                        {editingUserEid === user.eid ? (
                          <select
                            value={editFormData.right}
                            onChange={(e) => setEditFormData({ ...editFormData, right: e.target.value })}
                            className="px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="ADMIN">ADMIN</option>
                            <option value="EMPLOYEE">EMPLOYEE</option>
                            <option value="ONBOARDING">ONBOARDING</option>
                          </select>
                        ) : (
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            user.right === 'ADMIN' 
                              ? 'bg-purple-100 text-purple-800' 
                              : user.right === 'EMPLOYEE' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {user.right}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {editingUserEid === user.eid ? (
                          <select
                            value={editFormData.is_active ? 'true' : 'false'}
                            onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.value === 'true' })}
                            className="px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </select>
                        ) : (
                          user.is_active ? (
                            <span className="flex items-center gap-1 text-green-600">
                              <Check className="w-5 h-5" />
                              <span className="text-xs">Active</span>
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-600">
                              <X className="w-5 h-5" />
                              <span className="text-xs">Inactive</span>
                            </span>
                          )
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center gap-2">
                          {editingUserEid === user.eid ? (
                            <>
                              <button
                                onClick={() => handleSaveEdit()}
                                disabled={loading}
                                className="flex items-center gap-1 text-green-600 hover:text-green-800 disabled:text-gray-400"
                              >
                                <Save className="w-4 h-4" />
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="flex items-center gap-1 text-gray-600 hover:text-gray-800"
                              >
                                <X className="w-4 h-4" />
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEditClick(user)}
                                className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800"
                              >
                                <Edit2 className="w-4 h-4" />
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteUser(user.eid)}
                                disabled={loading}
                                className="flex items-center gap-1 text-red-600 hover:text-red-800 disabled:text-gray-400"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}